import * as React from "react"
import { router } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { team_roles_path } from "@/lib/routes"
import RoleList from "./components/RoleList"
import RoleDetail from "./components/RoleDetail"
import type { Role, Catalog, Member, OrgTreeNode } from "./types"

export default function RolesIndex({ roles, permission_catalog, members, org_tree, can }: {
  roles: Role[]
  permission_catalog: Catalog
  members: Member[]
  org_tree: OrgTreeNode[]
  can: { create: boolean; assign: boolean }
}) {
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [creating, setCreating] = React.useState(false)

  // Fall back to the first role when nothing is selected yet or the selected
  // role was just deleted.
  const selected = roles.find((r) => r.id === selectedId) ?? roles[0] ?? null

  const create = (name: string) => {
    const prevIds = new Set(roles.map((r) => r.id))
    setCreating(true)
    router.post(
      team_roles_path(),
      { role: { name, permission_keys: [] } },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          const next = (page.props.roles as Role[]).find((r) => !prevIds.has(r.id))
          if (next) setSelectedId(next.id)
        },
        onFinish: () => setCreating(false),
      }
    )
  }

  return (
    <AppLayout header={<PageTitle title="Roles" subtitle="Permissions per role" />}>
      <div className="flex items-start gap-4">
        <div className="w-72 shrink-0">
          <RoleList
            roles={roles}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
            canCreate={can.create}
            onCreate={create}
            creating={creating}
          />
        </div>
        <div className="flex-1 min-w-0">
          {selected && (
            <RoleDetail
              key={selected.id}
              role={selected}
              catalog={permission_catalog}
              members={members}
              orgTree={org_tree}
              canAssign={can.assign}
            />
          )}
        </div>
      </div>
    </AppLayout>
  )
}
